import React from "react";

// Shared visuals for the North Star (WEW) command center. Inline SVG/CSS only —
// no chart library (keeps the bundle lib-free; see the redesign ADR note).

// pass = green, warn = amber, alarm = red. Derived from the value if the
// backend doesn't supply an explicit status.
const STATUS_COLOR = {
  pass:  { fg: "var(--bl-success-soft, #2BD56F)", bg: "rgba(0,205,82,0.14)",  bar: "#2BD56F" },
  warn:  { fg: "#ffd199",                          bg: "rgba(243,175,76,0.16)", bar: "#f3af4c" },
  alarm: { fg: "#ff8a98",                          bg: "rgba(255,93,108,0.16)", bar: "#ff5d6c" },
};

export function deriveStatus({ current, target, alarmBelow }) {
  if (current == null) return "warn";
  if (alarmBelow != null && current < alarmBelow) return "alarm";
  if (target != null && current >= target) return "pass";
  return "warn";
}

function fmtByUnit(v, unit) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  if (unit === "ratio") {
    const s = (n * 100).toFixed(1);
    return (s.endsWith(".0") ? s.slice(0, -2) : s) + "%"; // "10%" not "10.0%"
  }
  if (unit === "usd") return "$" + n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return n.toLocaleString();
}

/**
 * A single 90-day pass-condition gauge: current value vs a target, with a
 * progress bar, a target tick, and a pass/warn/alarm tint.
 */
export function GaugeTile({ label, current, target, unit = "count", status, alarmBelow = null }) {
  const st = status || deriveStatus({ current, target, alarmBelow });
  const c = STATUS_COLOR[st] || STATUS_COLOR.warn;
  const hasData = current != null;

  // Scale the track so overshoot past target is visible; target tick sits at
  // its proportional position.
  const scaleMax = Math.max(Number(current) || 0, Number(target) || 0) * 1.15 || 1;
  const fillPct = hasData ? Math.min(100, Math.max(2, (Number(current) / scaleMax) * 100)) : 0;
  const tickPct = target != null ? Math.min(100, (Number(target) / scaleMax) * 100) : null;

  return (
    <div className="bl-glass" style={{ padding: "1.1rem 1.2rem" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: c.fg, background: c.bg, padding: "2px 7px", borderRadius: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {hasData ? st : "no data"}
        </span>
      </div>
      <div style={{ marginTop: 6, fontSize: "1.6rem", fontFamily: "var(--bl-font-numeric)", color: "#fff", fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
        {hasData ? fmtByUnit(current, unit) : "—"}
      </div>
      <div style={{ marginTop: 2, fontSize: 12, color: "var(--bl-text-soft)" }}>
        target {fmtByUnit(target, unit)}
      </div>
      {/* track + fill + target tick */}
      <div style={{ position: "relative", height: 8, marginTop: 10, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "visible" }}>
        <div style={{ width: `${fillPct}%`, height: "100%", background: c.bar, borderRadius: 4, transition: "width 240ms ease" }} />
        {tickPct != null && (
          <div title="target" style={{ position: "absolute", top: -3, left: `${tickPct}%`, width: 2, height: 14, background: "var(--bl-text-strong, #fff)", opacity: 0.7, transform: "translateX(-1px)" }} />
        )}
      </div>
    </div>
  );
}

/**
 * W1 retention cohort heatmap. Rows = cohort weeks, columns = week offsets
 * (W0..Wn). Cell background opacity scales with the retention value.
 * cohorts: [{ cohort_week, size, retention: [1.0, 0.34, ...] }]
 */
export function CohortHeatmap({ cohorts = [] }) {
  if (!cohorts.length) return null;
  const maxCols = Math.max(...cohorts.map((c) => (c.retention || []).length), 1);
  const cell = (v) => {
    if (v == null) return { bg: "transparent", fg: "var(--bl-text-faint, #787878)", txt: "" };
    const op = Math.max(0.06, Math.min(0.92, v)); // opacity ∝ retention
    return { bg: `rgba(87,88,252,${op.toFixed(3)})`, fg: v > 0.45 ? "#fff" : "var(--bl-text-strong, #eee)", txt: `${Math.round(v * 100)}%` };
  };
  const fmtWeek = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const th = { padding: "0.45rem 0.6rem", fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600, textAlign: "center", whiteSpace: "nowrap" };
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "separate", borderSpacing: 4, width: "100%", minWidth: 480 }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: "left" }}>Cohort week</th>
            <th style={{ ...th, textAlign: "right" }}>Size</th>
            {Array.from({ length: maxCols }).map((_, i) => (
              <th key={i} style={th}>W{i}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohorts.map((co) => (
            <tr key={co.cohort_week}>
              <td style={{ padding: "0.4rem 0.6rem", fontSize: 13, color: "var(--bl-text-strong)", whiteSpace: "nowrap" }}>{fmtWeek(co.cohort_week)}</td>
              <td style={{ padding: "0.4rem 0.6rem", fontSize: 13, color: "var(--bl-text-soft)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{co.size != null ? Number(co.size).toLocaleString() : "—"}</td>
              {Array.from({ length: maxCols }).map((_, i) => {
                const v = (co.retention || [])[i];
                const s = cell(v);
                return (
                  <td key={i} style={{ background: s.bg, color: s.fg, textAlign: "center", borderRadius: 6, fontSize: 12, padding: "0.5rem 0.4rem", fontVariantNumeric: "tabular-nums", minWidth: 44 }}>
                    {s.txt}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
